import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChannelType } from "../../models/SKChannel";
import { CreateChannelContext } from "../../context/CreateChannelContext";
import { useNavigationBar } from "../../context/NavigationContext";
import { useAuth } from "../../context/AuthContext";
import { AddTextField } from "../../components/AddTextField";
import OptionSelect from "../../components/OptionSelect";
import {
  ActionItem,
  ActionOnItem,
  actionToStateMap,
  ApplicantType,
  ApplicationForm,
  applicationFormFieldsOrdered,
  ApplicationFormSchema,
  ApplicationState,
  AudienceAgeRange,
  AudienceSize,
  EmployeeRange,
  Frequency,
  SensitiveTopic,
  TeamSize,
} from "../../models/SKApplication";
import { TopicType, TopicTypeString } from "../../models/SKArtifact";
import SingleSelect from "../../components/SingleSelect";
import { customCountryNames, List_of_countries_to_be_displayed, List_of_languages_to_be_displayed } from "../../utils/countryCodes";
import { iso31661 } from "iso-3166";
import { AddTextAreaField } from "../../components/AddTextAreaField";
import { SKSelectOption } from "../../components/SKSelectOption";
import { SKMultiSelect } from "../../components/SKMultiSelect";
import MultiSelectCounterDropdown from "../../components/MultiSelectCounterDropdown";
import { capitalizeFirstLetter } from "../../utils/stringFunctions";
import { iso6392 } from "iso-639-2";
import * as Yup from "yup";
import { getApplication, saveApplication, updateApplication, updateApplicationStatus } from "../../api/application/applicationServices";
import e from "express";
import { auth } from "../../firebase/firebase";
import { get } from "http";
import ArtifactStateDialog from "../channelManagement/ArtifactStateDialog";
import { DialoageContent } from "../artifacts/create/container/components/ArtifactStateDialog";
import ApplicationStateDialog from "./ApplicationStateDialog";
import APILoader from "../../components/APILoader";
import { PrimaryExternalLinkData } from "../../utils";
const channelTypes = [
  {
    id: ChannelType.brand,
    imgSrc: "/icons/Brands.png",
    title: "Organisations",
    desc: "Organization, business, company, institution, publisher, studio, agency",
  },
  {
    id: ChannelType.creator,
    imgSrc: "/icons/Creators.png",
    title: "Creator",
    desc: "Content creator, storyteller, influencer, writer, podcaster, blogger, artist, photographer",
  },
  // { id: "moderator", imgSrc: "/icons/Moderators.png", title: "Moderators", desc: "Review and manage content for safety" },
  // Add more items as needed
];

const initialState: ApplicationForm = {
  fullName: "",
  email: "",
  applicantType: ApplicantType.Creator,

  // BRAND specific (optional based on applicantType)
  legalEntityName: "",
  numEmployees: undefined,
  website: "",

  // CREATOR specific (optional based on applicantType)
  individualOrTeam: "individual",
  numTeamMembers: undefined,

  country: "USA",
  bio: "",
  socialLinks: [
    {
      type: "",
      value: "",
    },
  ],
  audienceSize: AudienceSize.Under100,
  banned: false,
  bannedExplain: "",

  channelName: "",
  channelSummery: "",
  contentConcept: "",
  contentType: "Free",
  frequency: Frequency.Weekly,
  audienceAge: AudienceAgeRange.AllAges,

  primaryCategory: TopicType.education,
  secondaryCategories: [],

  primaryLanguage: "eng",
  audienceLocation: "",

  whySkarvo: "",
  valueToFollowers: "",
  engagementStrategy: "",
  onlineSafety: "",
  sensitiveTopics: [],

  hasCollaborators: false,
  moderationGuidelines: "",

  hasRunPaidCommunity: false,
  paidCommunityExplain: "",

  marketingStrategy: "",
  promotionStrategy: "",
  thirdPartySponsorships: false,
  skarvoPaidAds: false,
  interestedInPartnerships: false,
  agreesToGuidelines: false,
  confirmsLegalCompliance: false,
  contentIsSafe: false,
  referralSource: "",
  status: ApplicationState.DRAFT,
};

const sortedExternalLinkOptions = PrimaryExternalLinkData.map(link => ({
  title: link.title,
  value: link.type,
}));

function ApplicationDetail() {
  const location = useLocation();
  const { id } = useParams();
  // const [selected, setSelected] = useState<string | null>(location.state?.userType);

  const [formData, setFormData] = useState<ApplicationForm>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidForm, setIsValidForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actionOnItem, setActionOnItem] = useState<ActionOnItem | null>(null);
  const [dialogContent, setDialogContent] = useState<DialoageContent | null>(null);

  const disabled = true;

  const handleChange = (field: keyof ApplicationForm, value: any) => {
    setErrors({});
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: "" }); // Clear error on change
  };

  useEffect(() => {
    const getApp = async (uid: string) => {
      try {
        const data = await getApplication(uid);
        setFormData(data);
      } catch (error) {
        console.error("Error fetching application data:", error);
      }
    };
    if (id) {
      getApp(id);
    }
  }, [id]);

  const validateForm = async (name: string, value: any) => {
    try {
      await ApplicationFormSchema.validate({ [name]: value }, { abortEarly: false });
      return true;
    } catch (err: any) {
      const newErrors: Record<string, string> = {};
      if (err.inner && err.inner.length > 0) {
        err.inner.forEach((e: any) => {
          if (e.path === name) newErrors[e.path] = e.message;
        });
      } else if (err.path) {
        newErrors[err.path] = err.message;
      }
      setErrors(newErrors);
      return false;
    }
  };

  const validateFormUpTo = useCallback(
    async (currentField: keyof ApplicationForm) => {
      const index = applicationFormFieldsOrdered.indexOf(currentField);
      if (index === -1) return;

      const fieldsToValidate = applicationFormFieldsOrdered.slice(0, index + 1);
      const schemaSlice = ApplicationFormSchema.pick(fieldsToValidate);

      try {
        await schemaSlice.validate(formData, { abortEarly: false });
        setErrors({});
      } catch (err) {
        const validationErrors: { [key: string]: string } = {};
        if (err instanceof Yup.ValidationError) {
          err.inner.forEach(e => {
            if (e.path && !validationErrors[e.path]) {
              validationErrors[e.path] = e.message;
            }
          });
        }
        setErrors(validationErrors);
      }
    },
    [formData]
  );

  useEffect(() => {
    const validateAll = async () => {
      try {
        await ApplicationFormSchema.validate(formData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err: any) {
        setIsValidForm(false);
        return false;
      }
    };
    validateAll();
  }, [formData]);

  const { setNavProps } = useNavigationBar();
  let context = useAuth();
  const navigate = useNavigate();

  // useEffect(() => {
  //   const getRole = async () => {
  //     let result = await context?.currentUser?.getIdTokenResult();
  //     let claims = result?.claims;
  //   };
  //   getRole();
  // }, []);

  // useEffect(() => {
  //   setNavProps({
  //     backArrow: false,
  //     title: "Channel",
  //     subtitle: "Onboarding",
  //     buttonTitle: "Submit",
  //     filterOption: false,
  //     enableLink: isValidForm,
  //     onNext: !disabled
  //       ? async () => {
  //           await handleSubmit();
  //           navigate(-1);
  //         }
  //       : undefined,
  //     onClose: async () => {
  //       !disabled && (await saveApp());
  //       navigate(-1);
  //     },
  //   });
  // }, [setNavProps, handleSubmit, saveApp, isValidForm]);

  const getPopItemsForId = (status: ApplicationState): ActionItem[] => {
    switch (status) {
      case ApplicationState.APPROVED:
        return [];

      case ApplicationState.REJECTED:
        return [ActionItem.approve];

      case ApplicationState.SUBMITTED:
        return [ActionItem.approve, ActionItem.reject];

      default:
        return [];
    }
  };
  useEffect(() => {
    formData?.id &&
      setNavProps({
        backArrow: false,
        title: "Application",
        subtitle: "Channel",
        buttonTitle: "",
        enableLink: true,
        getActionItems: () => getPopItemsForId(formData.status),
        onActionItemSelect: actionItem => {
          setActionOnItem({
            itemid: formData.id!,
            action: actionItem as ActionItem,
          });
        },
        onClose: () => {
          navigate(-1);
        },
      });
  }, [setNavProps, formData]);

  // useEffect(() => {
  //   setSelected(location.state?.userType);
  // }, [location]);

  const isoLanguages = [
    { title: "English", value: "eng" },
    ...iso6392
      .filter(lang => List_of_languages_to_be_displayed.includes(lang.name))
      .map(lang => ({
        title: lang.name,
        value: lang.iso6392B,
      }))
      .sort((a, b) => a.title.localeCompare(b.title)),
  ];

  const isoCountries = [
    { title: "United States", value: "USA" },
    ...iso31661
      .filter(country => List_of_countries_to_be_displayed.includes(country.name))
      .map(country => ({
        title: customCountryNames[country.name] || country.name,
        value: country.alpha3,
      }))
      .sort((a, b) => a.title.localeCompare(b.title)),
  ];

  const showStatus = useCallback(() => {
    if (formData.status === ApplicationState.APPROVED) {
      return "Approved";
    } else if (formData.status === ApplicationState.REJECTED) {
      return "Rejected";
    } else if (formData.status === ApplicationState.DRAFT) {
      return "Draft";
    } else if (formData.status === ApplicationState.SUBMITTED) {
      return "In Review";
    }
  }, [formData]);

  const statusImage = useCallback(() => {
    if (formData.status === ApplicationState.APPROVED) {
      return "/icons/CheckItem.png";
    } else if (formData.status === ApplicationState.REJECTED) {
      return "/icons/rejectItem.png";
    } else if (formData.status === ApplicationState.DRAFT) {
      return "/icons/draftItem.png";
    } else if (formData.status === ApplicationState.SUBMITTED) {
      return "/icons/inReviewItem.png";
    }
  }, [formData]);

  return (
    <div className="bg-white flex flex-col gap-[30px] mb-16">
      <APILoader loading={isSaving} />
      {actionOnItem?.action ? (
        <ApplicationStateDialog
          action={actionOnItem?.action}
          onClose={() => setActionOnItem(null)}
          onUpdate={async () => {
            setActionOnItem(null);
            setIsSaving(true);
            const action = actionOnItem?.action;
            const contentId = actionOnItem?.itemid;
            //await updateStatus(contentId, action);
            await updateApplicationStatus(contentId, actionToStateMap(action));
            setIsSaving(false);
            navigate(-1);
          }}
        />
      ) : (
        <></>
      )}

      <div className="flex items-center gap-5 justify-end mt-[5px] mr-3">
        <div className="flex items-center gap-1">
          <p className="ml-1 capitalize">{showStatus()}</p>
          <span>
            <img src={statusImage()} className="object-contain w-[14px] h-[14px] min-w-5" alt="Certification status" />
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-[10px]">
          <span className="content_title !text-[25px]">Applicant Information</span>
          <div className=" bg-skseparatorgray w-full rounded-full h-[5px]" />
        </div>
        <AddTextField
          name={"name"}
          title={"Your Name"}
          note={"Your full legal name."}
          placeholder={"Name"}
          index={1}
          charAllowed={50}
          value={formData.fullName}
          onChange={async e => {
            setFormData(prev => ({ ...prev, fullName: e.target.value }));
            // updateContentData({ title: e.target.value! });
          }}
          disabled={disabled}
          error={errors.fullName}
          onBlur={() => validateFormUpTo("fullName")}
          //onFocus={() => setErrors({})}
        />
        <AddTextField
          name={"email"}
          title={"Email Address"}
          note={"We’ll use this to contact you about your application."}
          placeholder={"Name"}
          index={2}
          charAllowed={50}
          value={formData.email}
          onChange={async e => {
            setFormData(prev => ({ ...prev, email: e.target.value }));
          }}
          disabled={disabled}
          error={errors.email}
          onBlur={() => validateFormUpTo("email")}
          //onFocus={() => setErrors({})}
        />

        <OptionSelect
          index={3}
          title="Are You a Creator or Brand?"
          subTitle="If you’re applying as a creator or on behalf of a brand."
          value={formData.applicantType}
          options={[
            { id: 1, title: "Creator", value: ApplicantType.Creator, description: "Creator" },
            { id: 2, title: "Brand (Business or Organization)", value: ApplicantType.Brand, description: "Brand (Business or Organization)" },
          ]}
          onSelect={value => {
            setFormData(prev => ({ ...prev, applicantType: value as ApplicantType }));
          }}
          error={errors.applicantType}
          disabled={disabled}
        />

        {formData.applicantType === ApplicantType.Creator && (
          <OptionSelect
            title="Applying as an Individual or Team?"
            value={formData.individualOrTeam ?? "individual"}
            options={[
              { id: 1, title: "Individual", value: "individual" },
              { id: 2, title: "Team", value: "team" },
            ]}
            onSelect={value => setFormData(prev => ({ ...prev, individualOrTeam: value as "individual" | "team" }))}
            error={errors.individualOrTeam}
            disabled={disabled}
          />
        )}

        {formData.applicantType === ApplicantType.Creator && (
          <SingleSelect
            title="Team Members"
            options={Object.entries(TeamSize).map(([key, value]) => ({
              label: value,
              value,
            }))}
            selected={formData.numTeamMembers}
            onChange={val => setFormData(prev => ({ ...prev, numTeamMembers: val as TeamSize }))}
            error={errors.audienceAge}
            disabled={disabled}
          />
        )}

        {formData.applicantType === ApplicantType.Brand && (
          <AddTextField
            name={"legalEntityName"}
            title={"Registered Legal Name"}
            placeholder={"Registered Legal Name"}
            charAllowed={100}
            value={formData.legalEntityName}
            onChange={async e => {
              setFormData(prev => ({ ...prev, legalEntityName: e.target.value }));
              // updateContentData({ title: e.target.value! });
            }}
            onBlur={() => validateFormUpTo("legalEntityName")}
            error={errors.legalEntityName}
            disabled={disabled}
            //onFocus={() => setErrors({})}
          />
        )}

        {formData.applicantType === ApplicantType.Brand && (
          <AddTextField
            name={"website"}
            title={"Website"}
            placeholder={"Registered Legal Name"}
            charAllowed={100}
            value={formData.website}
            onChange={async e => {
              setFormData(prev => ({ ...prev, website: e.target.value }));
              // updateContentData({ title: e.target.value! });
            }}
            onBlur={() => validateFormUpTo("website")}
            disabled={disabled}
            error={errors.website}
            //onFocus={() => setErrors({})}
          />
        )}

        {formData.applicantType === ApplicantType.Brand && (
          <SingleSelect
            title="Number of employees in your company?"
            options={Object.entries(EmployeeRange).map(([key, value]) => ({
              label: value,
              value,
            }))}
            selected={formData.numEmployees}
            onChange={val => setFormData(prev => ({ ...prev, numEmployees: val as EmployeeRange }))}
            error={errors.numEmployees}
            disabled={disabled}
          />
        )}

        <SingleSelect
          index={4}
          title="Your Country"
          note="Where you are based."
          options={isoCountries.map(obj => ({
            label: obj.title,
            value: obj.value,
          }))}
          selected={formData.country}
          onChange={val => setFormData(prev => ({ ...prev, country: val }))}
          error={errors.country}
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-[10px]">
          <span className="content_title !text-[25px]">Digital Presence</span>
          <div className=" bg-skseparatorgray w-full rounded-full h-[5px]" />
        </div>
        <AddTextAreaField
          name={"bio"}
          title={"About You"}
          note={"Briefly describe who you are and what you do."}
          placeholder={"Description"}
          height={100}
          index={1}
          charAllowed={500}
          value={formData.bio}
          onChange={async e => {
            setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
          }}
          onBlur={async e => {
            validateFormUpTo("bio");
          }}
          disabled={disabled}
          error={errors.bio}

          //onFocus={() => setErrors({})}
        />

        {/* // External Link */}
        <div className="flex flex-col gap-[30px]">
          <div className="flex flex-col w-full">
            <div className="relative inline-block w-full text-left">
              <SKSelectOption
                index={2}
                id="Link_1"
                value={(formData.socialLinks[0] && formData.socialLinks[0].type) ?? "web_social"}
                // value={
                //   formData.socialLinks?.[0]?.type ||
                //   sortedExternalLinkOptions[0].value
                // }
                heading="Social Media Links"
                subHeading="Add up to 3 links to your most active social profiles or wesbites."
                width="w-[290px]"
                options={[{ title: "Website", value: "web_social" }, ...sortedExternalLinkOptions]}
                //options={sortedExternalLinkOptions}
                selectedIndex={1}
                onUpdate={e => {
                  const { value } = e.target;
                  let socialLinks = formData.socialLinks || [];
                  socialLinks[0] = {
                    type: value,
                    value: socialLinks[0]?.value || "",
                  };
                  setFormData(prev => ({ ...prev, socialLinks }));
                }}
                marginBottom="mb-[15px]"
                editable={!disabled}
              />
            </div>
            <div className="flex items-center">
              <AddTextField
                id={"primaryExternalLink"}
                name={"primaryExternalLink"}
                title={""}
                note={""}
                placeholder={"Add Link"}
                charAllowed={50}
                value={formData.socialLinks && formData.socialLinks.length > 0 ? formData.socialLinks[0].value ?? "" : ""}
                isCloseBtn={false}
                onChange={async e => {
                  const { name, value } = e.target;
                  let socialLinks = formData.socialLinks;
                  if (socialLinks && socialLinks.length >= 1) {
                    socialLinks[0] = {
                      type: socialLinks[0].type,
                      value: value,
                    };
                  }
                  setFormData(prev => ({
                    ...prev,
                    socialLinks,
                  }));
                }}
                showRemainingChar={false}
                showError={false}
                disabled={disabled}
                //onFocus={() => setErrors({})}
              />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <div className="relative inline-block w-8/12 text-left">
              <SKSelectOption
                id="Link_2"
                value={formData.socialLinks?.[1]?.type || "Optional"}
                selectedIndex={2}
                heading=""
                subHeading={""}
                width={"w-[290px]"}
                options={[{ title: "Optional", value: "Optional" }, ...sortedExternalLinkOptions]}
                onUpdate={e => {
                  const { name, value } = e.target;
                  let socialLinks = formData.socialLinks;
                  if (socialLinks && socialLinks.length >= 2) {
                    socialLinks[1] = {
                      type: value,
                      value: socialLinks[1].value,
                    };
                  } else {
                    socialLinks?.push({ type: value, value: "" });
                  }
                  setFormData(prev => ({
                    ...prev,
                    socialLinks,
                  }));
                }}
                marginBottom="mb-[15px]"
                editable={!disabled}
              />
            </div>

            <div className="flex items-center">
              <AddTextField
                id={"secondaryExternalLink"}
                name={"secondaryExternalLink"}
                title={""}
                note={""}
                placeholder={"Add Link"}
                charAllowed={50}
                value={formData.socialLinks?.[1]?.value ?? ""}
                isCloseBtn={false}
                onChange={async e => {
                  const { name, value } = e.target;
                  let socialLinks = formData.socialLinks;
                  if (socialLinks && socialLinks.length >= 2) {
                    socialLinks[1] = {
                      type: socialLinks[1].type,
                      value: value,
                    };
                  }
                  setFormData(prev => ({
                    ...prev,
                    socialLinks,
                  }));
                }}
                showRemainingChar={false}
                showError={false}
                disabled={disabled}
                //onFocus={() => setErrors({})}
              />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <div className="relative inline-block w-8/12 text-left">
              <SKSelectOption
                id="Link_3"
                value={formData.socialLinks?.[2]?.type || "Optional"}
                selectedIndex={3}
                heading=""
                subHeading={""}
                width={"w-[290px]"}
                options={[{ title: "Optional", value: "Optional" }, ...sortedExternalLinkOptions]}
                onUpdate={e => {
                  const { name, value } = e.target;
                  let socialLinks = formData.socialLinks;
                  if (socialLinks && socialLinks?.length >= 3) {
                    socialLinks[2] = {
                      type: value,
                      value: socialLinks[2].value,
                    };
                  } else {
                    socialLinks?.push({ type: value, value: "" });
                  }
                  setFormData(prev => ({
                    ...prev,
                    socialLinks,
                  }));
                }}
                marginBottom="mb-[15px]"
                editable={!disabled}
              />
            </div>
            <div className="flex items-center">
              <AddTextField
                id={"otherExternalLink"}
                name={"otherExternalLink"}
                title={""}
                note={""}
                placeholder={"Add Link"}
                charAllowed={50}
                value={formData.socialLinks?.[2]?.value ?? ""}
                isCloseBtn={false}
                onChange={async e => {
                  const { name, value } = e.target;
                  let socialLinks = formData.socialLinks;
                  if (socialLinks && socialLinks?.length >= 3) {
                    socialLinks[2] = { type: socialLinks[2].type, value };
                  }
                  setFormData(prev => ({
                    ...prev,
                    socialLinks,
                  }));
                }}
                showRemainingChar={false}
                error={errors.externalLinks}
                disabled={disabled}
                //onFocus={() => setErrors({})}
              />
            </div>
          </div>
        </div>
        {/* // ExternalLink End  */}

        <SingleSelect
          index={3}
          title="Audience Size"
          note="Estimate your current following across all platforms."
          options={Object.entries(AudienceSize).map(([key, value]) => ({
            label: value,
            value,
          }))}
          selected={formData.audienceSize}
          onChange={val => setFormData(prev => ({ ...prev, audienceSize: val as AudienceSize }))}
          error={errors.audienceSize}
          disabled={disabled}
        />

        <OptionSelect
          index={4}
          title="Have you ever been Banned or Flagged on other platforms?"
          subTitle="Be honest, we just want to understand context."
          value={formData.banned}
          options={[
            { id: 1, title: "Yes", value: true },
            { id: 2, title: "No", value: false },
          ]}
          onSelect={value => {
            setFormData(prev => ({ ...prev, banned: value as boolean }));
          }}
          disabled={disabled}
        />
        {formData.banned && (
          <AddTextAreaField
            name={"bannedExplain"}
            title={"Tell Us More"}
            placeholder={"Explain"}
            height={80}
            charAllowed={200}
            value={formData.bio}
            onChange={async e => {
              setFormData(prev => ({ ...prev, bannedExplain: e.target.value }));
            }}
            onBlur={async e => {
              validateFormUpTo("bannedExplain");
            }}
            disabled={disabled}
            error={errors.bannedExplain}
            //onFocus={() => setErrors({})}
          />
        )}
      </div>
      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-[10px]">
          <span className="content_title !text-[25px]">Channel Details</span>
          <div className=" bg-skseparatorgray w-full rounded-full h-[5px]" />
        </div>

        <AddTextField
          index={1}
          name="channelName"
          title="Channel Name"
          note="What would you like to name your channel on Skarvo."
          placeholder="A short summary of what your channel is about."
          charAllowed={100}
          value={formData.channelName}
          onChange={e => handleChange("channelName", e.target.value)}
          disabled={disabled}
          error={errors.channelName}
          onBlur={() => validateFormUpTo("channelName")}
          //onFocus={() => setErrors({})}
        />

        <AddTextField
          index={2}
          name="channelSummery"
          title="Channel Overview"
          note="A short summary of what your channel is about."
          placeholder="A short summary of what your channel is about."
          charAllowed={100}
          value={formData.channelSummery}
          onChange={e => handleChange("channelSummery", e.target.value)}
          disabled={disabled}
          error={errors.channelSummery}
          onBlur={() => validateFormUpTo("channelSummery")}
          //onFocus={() => setErrors({})}
        />

        <AddTextAreaField
          index={3}
          name="contentConcept"
          title="What kind of content will you publish?"
          note="Describe the topics and formats you plan to share."
          placeholder="Describe the topics and formats you plan to share."
          charAllowed={200}
          height={80}
          value={formData.contentConcept}
          onChange={e => handleChange("contentConcept", e.target.value)}
          disabled={disabled}
          error={errors.contentConcept}
          onBlur={() => validateFormUpTo("contentConcept")}
          //onFocus={() => setErrors({})}
        />
        <OptionSelect
          index={4}
          title="Content Type"
          subTitle="How do you plan to monetize you content."
          value={formData.contentType}
          options={[
            { id: 1, title: "Free", value: "Free" },
            { id: 2, title: "Subscription", value: "Paid" },
            { id: 3, title: "Free and Subscription Both", value: "Both Free and Paid" },
          ]}
          onSelect={value => {
            setFormData(prev => ({ ...prev, contentType: value as string }));
          }}
          error={errors.contentType}
          disabled={disabled}
        />

        <SingleSelect
          index={5}
          title="Posting Frequency"
          note="How regularly you’ll publish content."
          options={Object.entries(Frequency).map(([key, value]) => ({
            label: value,
            value,
          }))}
          selected={formData.frequency}
          onChange={val => setFormData(prev => ({ ...prev, frequency: val as Frequency }))}
          error={errors.frequency}
          disabled={disabled}
        />
        <SingleSelect
          index={6}
          title="Audience Age Range"
          note="Who is your content primarily created for?"
          options={Object.entries(AudienceAgeRange).map(([key, value]) => ({
            label: value,
            value,
          }))}
          selected={formData.audienceAge}
          onChange={val => setFormData(prev => ({ ...prev, audienceAge: val as AudienceAgeRange }))}
          error={errors.audienceAge}
          disabled={disabled}
        />

        <SingleSelect
          index={7}
          title="Main Content Category"
          note="Choose the category that best fits your main content type."
          options={Object.entries(TopicType).map(([key, value]) => {
            const val = TopicTypeString[value];
            return {
              label: val,
              value,
            };
          })}
          selected={formData.primaryCategory}
          onChange={val => handleChange("primaryCategory", val)}
          error={errors.primaryCategory}
          disabled={disabled}
        />

        <MultiSelectCounterDropdown
          index={8}
          title="Secondary Content Categories"
          note="Choose any other categories your content may include."
          options={Object.entries(TopicType).map(([key, value]) => {
            const val = TopicTypeString[value];
            return {
              label: val,
              value,
            };
          })}
          selected={formData.secondaryCategories}
          onChange={(selected, _) => handleChange("secondaryCategories", selected)}
          error={errors.secondaryCategories}
        />

        <SKSelectOption
          index={9}
          heading="Main Language"
          subHeading="The main language your content will be in."
          value={formData.primaryLanguage}
          width={"w-[290px]"}
          options={isoLanguages}
          onUpdate={e => handleChange("primaryLanguage", e.target.value)}
          marginBottom=""
          onBlur={() => validateFormUpTo("primaryLanguage")}
          editable={!disabled}
        />

        <AddTextField
          index={10}
          name="audienceLocation"
          title="Audience Location"
          note="Region or global audience you are targeting."
          placeholder="e.g., Global, India, USA"
          charAllowed={100}
          value={formData.audienceLocation}
          onChange={e => handleChange("audienceLocation", e.target.value)}
          disabled={disabled}
          error={errors.audienceLocation}
          onBlur={() => validateFormUpTo("audienceLocation")}
          //onFocus={() => setErrors({})}
        />
      </div>

      {/* Channel Details */}
      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-[10px]">
          <span className="content_title !text-[25px]">Channel Content</span>
          <div className=" bg-skseparatorgray w-full rounded-full h-[5px]" />
        </div>
        <AddTextAreaField
          name="whySkarvo"
          title="Why Skarvo?"
          placeholder="What draws you to Skarvo as a platform?"
          charAllowed={200}
          height={80}
          value={formData.whySkarvo}
          onChange={e => handleChange("whySkarvo", e.target.value)}
          disabled={disabled}
          error={errors.whySkarvo}
          onBlur={() => validateFormUpTo("whySkarvo")}
          //onFocus={() => setErrors({})}
        />

        <AddTextAreaField
          name="valueToFollowers"
          title="Value to Followers"
          placeholder="How your content will help or inspire your audience."
          charAllowed={200}
          height={80}
          value={formData.valueToFollowers}
          onChange={e => handleChange("valueToFollowers", e.target.value)}
          disabled={disabled}
          error={errors.valueToFollowers}
          onBlur={() => validateFormUpTo("valueToFollowers")}
          //onFocus={() => setErrors({})}
        />

        <AddTextAreaField
          index={3}
          name="engagementStrategy"
          title="Audience Engagement"
          placeholder="How you’ll interact and build your channel community."
          charAllowed={200}
          height={80}
          value={formData.engagementStrategy}
          onChange={e => handleChange("engagementStrategy", e.target.value)}
          disabled={disabled}
          error={errors.engagementStrategy}
          onBlur={() => validateFormUpTo("engagementStrategy")}
          //onFocus={() => setErrors({})}
        />

        <AddTextAreaField
          index={4}
          name="onlineSafety"
          title="Your Views on Online Safety"
          note="Tell us your view on building safe online spaces."
          placeholder="Description"
          charAllowed={200}
          height={80}
          value={formData.onlineSafety}
          onChange={e => handleChange("onlineSafety", e.target.value)}
          disabled={disabled}
          error={errors.onlineSafety}
          onBlur={() => validateFormUpTo("onlineSafety")}
          //onFocus={() => setErrors({})}
        />

        <MultiSelectCounterDropdown
          index={5}
          title="Will your content include sensitive topics?"
          note="Select all that apply."
          options={Object.entries(SensitiveTopic).map(([key, value]) => ({
            label: value,
            value,
          }))}
          selected={formData.sensitiveTopics}
          onChange={selected => handleChange("sensitiveTopics", selected)}
          error={errors.sensitiveTopics}
          disabled={disabled}
        />

        <AddTextAreaField
          index={6}
          name="moderationGuidelines"
          title="Your Content Moderation Plan"
          placeholder="Do you have a way to keep content safe, respectful, and high quality?"
          charAllowed={200}
          height={80}
          value={formData.moderationGuidelines}
          onChange={e => handleChange("moderationGuidelines", e.target.value)}
          disabled={disabled}
          error={errors.moderationGuidelines}
          onBlur={() => validateFormUpTo("moderationGuidelines")}
          //onFocus={() => setErrors({})}
        />

        <OptionSelect
          index={7}
          title="Experience with Premium Content"
          subTitle="Do you have past experience managing premium channels or subscription-based communities?"
          value={formData.hasRunPaidCommunity}
          options={[
            { id: 1, title: "Yes", value: true },
            { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("hasRunPaidCommunity", val)}
          disabled={disabled}
        />

        {formData.hasRunPaidCommunity && (
          <AddTextAreaField
            name="paidCommunityExplain"
            title="Tell Us More"
            placeholder="Description"
            charAllowed={200}
            value={formData.paidCommunityExplain ?? ""}
            height={80}
            onChange={e => handleChange("paidCommunityExplain", e.target.value)}
            disabled={disabled}
            error={errors.paidCommunityExplain}
            onBlur={() => validateFormUpTo("paidCommunityExplain")}
            //onFocus={() => setErrors({})}
          />
        )}
        <OptionSelect
          index={8}
          title="Any Co-hosts or Collaborators?"
          subTitle="Do you have collaborators, co-hosts, or guests?"
          value={formData.hasCollaborators}
          options={[
            { id: 1, title: "Yes", value: true },
            { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("hasCollaborators", val)}
          disabled={disabled}
        />

        {formData.hasCollaborators && (
          <AddTextAreaField
            name="collaboratorsExplain"
            title="Tell Us More"
            placeholder="Description"
            charAllowed={200}
            value={formData.collaboratorsExplain ?? ""}
            height={80}
            onChange={e => handleChange("collaboratorsExplain", e.target.value)}
            disabled={disabled}
            error={errors.collaboratorsExplain}
            onBlur={() => validateFormUpTo("collaboratorsExplain")}
            //onFocus={() => setErrors({})}
          />
        )}
      </div>
      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-[10px]">
          <span className="content_title !text-[25px]">Growing with Skarvo</span>
          <div className=" bg-skseparatorgray w-full rounded-full h-[5px]" />
        </div>
        <AddTextAreaField
          index={1}
          name="marketingStrategy"
          title="Bringing Your First Followers"
          note="How will you attract your initial audience to your Skarvo channel?"
          placeholder="Description"
          charAllowed={200}
          height={80}
          value={formData.marketingStrategy}
          onChange={e => handleChange("marketingStrategy", e.target.value)}
          disabled={disabled}
          error={errors.marketingStrategy}
          onBlur={() => validateFormUpTo("marketingStrategy")}
          //onFocus={() => setErrors({})}
        />

        <AddTextAreaField
          index={2}
          name="promotionStrategy"
          title="Promoting Your Channel on Other Platforms"
          note="Your plans to promote your channel on other platforms."
          placeholder="Description"
          charAllowed={200}
          height={80}
          value={formData.promotionStrategy}
          onChange={e => handleChange("promotionStrategy", e.target.value)}
          disabled={disabled}
          error={errors.promotionStrategy}
          onBlur={() => validateFormUpTo("promotionStrategy")}
          //onFocus={() => setErrors({})}
        />

        <OptionSelect
          index={3}
          title="Brand Sponsors in Your Content?"
          subTitle="If affiliate or brand sponsorships will be part of your channel?"
          value={formData.thirdPartySponsorships}
          options={[
            { id: 1, title: "Yes", value: true },
            { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("thirdPartySponsorships", val)}
          disabled={disabled}
        />
        {formData.thirdPartySponsorships && (
          <AddTextAreaField
            name={"sponsorExplain"}
            title={"Tell Us More"}
            placeholder={"Description"}
            height={80}
            charAllowed={200}
            value={formData.sponsorExplain ?? ""}
            onChange={async e => {
              setFormData(prev => ({ ...prev, sponsorExplain: e.target.value }));
            }}
            onBlur={async e => {
              validateFormUpTo("sponsorExplain");
            }}
            disabled={disabled}
            error={errors.sponsorExplain}
            //onFocus={() => setErrors({})}
          />
        )}

        <OptionSelect
          index={4}
          title="Interested in Paid Promotion?"
          subTitle="Are you open to paying for ads on Skarvo to promote your channel?"
          value={formData.skarvoPaidAds}
          options={[
            { id: 1, title: "Yes", value: true },
            { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("skarvoPaidAds", val)}
          disabled={disabled}
        />

        <OptionSelect
          title="Open to Partnerships?"
          subTitle="Will you like to collaborate with other creators, brands, or sponsors?"
          value={formData.interestedInPartnerships}
          options={[
            { id: 1, title: "Yes", value: true },
            { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("interestedInPartnerships", val)}
          disabled={disabled}
        />

        <OptionSelect
          index={6}
          title="Agree to Content Guidelines?"
          subTitle="Confirm that you’ve read and agree to our community guidelines."
          value={formData.agreesToGuidelines}
          options={[
            { id: 1, title: "I Agree", value: true },
            // { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("agreesToGuidelines", val)}
          disabled={disabled}
        />

        <OptionSelect
          title="Legal and Copyright Compliance"
          index={7}
          subTitle="Confirm that you’ve legal rights to everything you publish."
          value={formData.confirmsLegalCompliance}
          options={[
            { id: 1, title: "I Confirm", value: true },
            // { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("confirmsLegalCompliance", val)}
          disabled={disabled}
        />

        <OptionSelect
          index={8}
          title="Content intended to Educate, Entertain, Inspire, or Inform"
          subTitle="Confirm that your content is safe and has a positive purpose or message."
          value={formData.contentIsSafe}
          options={[
            { id: 1, title: "I Confirm", value: true },
            // { id: 2, title: "No", value: false },
          ]}
          onSelect={val => handleChange("contentIsSafe", val)}
          disabled={disabled}
        />

        <AddTextField
          index={9}
          name="referralSource"
          title="How did you hear about Skarvo?"
          note="How did you discover us?"
          placeholder="e.g., Twitter, Friend, Event"
          charAllowed={100}
          value={formData.referralSource}
          onChange={e => handleChange("referralSource", e.target.value)}
          disabled={disabled}
          error={errors.referralSource}
          onBlur={() => validateFormUpTo("referralSource")}
          //onFocus={() => setErrors({})}
        />
      </div>
    </div>
  );
}

export default ApplicationDetail;
